import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Play, Pause, Megaphone, Trash2, Download, ArrowLeft, ArrowRight } from "lucide-react";
import { api, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, Modal, Select, StatusPill, Toggle } from "@/components/bits";

const TARGETS = [5, 10, 15];

const emptyForm = {
  campaign_name: "",
  lead_type: "farmer",
  state: "",
  districts: [],
  agent_id: "",
  goal: "",
  success_target: 10,
  auto_pause: true,
  file: null,
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const fileSize = (bytes) => (!bytes ? "" : bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);

export default function Campaigns() {
  const { company } = useAuth();
  const [list, setList] = useState(null);
  const [agents, setAgents] = useState([]);
  const [opts, setOpts] = useState(null);
  const [modal, setModal] = useState(false);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => api.get("/campaigns").then((r) => setList(r.data)), []);
  useEffect(() => { load(); api.get("/agents").then((r) => setAgents(r.data)); api.get("/options").then((r) => setOpts(r.data)); }, [load]);
  useEffect(() => {
    if (!list?.some((c) => c.status === "running")) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [list, load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const close = () => { setModal(false); setStep(1); setForm(emptyForm); };

  const baseStates = company?.states?.length ? company.states : (opts?.states || []).map((s) => (typeof s === "string" ? s : s.name));
  const stateOptions = opts && !baseStates.includes("Bihar") ? [...baseStates, "Bihar"] : baseStates;
  const districtChoices = ((opts?.districts || {})[form.state] || []).map((d) => (Array.isArray(d) ? d[0] : d?.name)).filter(Boolean);
  const agentChoices = agents.filter((a) => a.audience === form.lead_type);
  const targetNumber = Number(form.success_target);
  const targetValid = Number.isInteger(targetNumber) && targetNumber > 0;
  const step1Valid = Boolean(form.campaign_name.trim() && form.state && form.agent_id && targetValid);
  const step2Valid = Boolean(form.file);

  const toggleDistrict = (name) => set({ districts: form.districts.includes(name) ? form.districts.filter((d) => d !== name) : [...form.districts, name] });

  const pickLeadType = (leadType) => {
    const keepAgent = form.agent_id && agents.find((a) => a.id === form.agent_id)?.audience === leadType ? form.agent_id : "";
    set({ lead_type: leadType, agent_id: keepAgent });
  };

  const create = async (run) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("campaign_name", form.campaign_name.trim());
      body.append("agent_id", form.agent_id);
      body.append("lead_type", form.lead_type);
      body.append("state", form.state);
      body.append("districts", form.districts.join(","));
      body.append("goal", form.goal.trim());
      body.append("success_target", String(form.success_target));
      body.append("auto_pause_on_target", form.auto_pause ? "1" : "0");
      body.append("file", form.file);
      const { data } = await api.post("/campaigns", body);
      const leads = data.total ?? data.lead_count ?? 0;
      const skipped = data.skipped_rows ?? 0;
      const tail = `${leads} lead${leads === 1 ? "" : "s"} uploaded${skipped ? ` · ${skipped} rows skipped` : ""}`;
      if (run) {
        await api.post(`/campaigns/${data.id}/run`);
        toast.success(`Campaign running — ${tail}`);
      } else toast.success(`Draft saved — ${tail}`);
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the campaign");
    } finally {
      setBusy(false);
    }
  };

  const ctl = async (id, action) => {
    try {
      await api.post(`/campaigns/${id}/${action}`);
      toast.success(action === "pause" ? "Campaign paused" : action === "resume" ? "Campaign resumed" : "Campaign started");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/campaigns/${id}`); load(); };

  return (
    <div>
      <PageHeader overline="Outreach" title="Campaigns" desc="Agent × audience × region. WhatsApp first nudge, then the dialect voice call — judged against the campaign goal." testId="campaigns-header"
        action={<button onClick={() => setModal(true)} className="btn-primary" data-testid="campaigns-new-btn"><Plus className="w-4 h-4" /> New campaign</button>} />
      {list && list.length === 0 && <Empty title="No campaigns yet" desc="Name the campaign, set the goal and upload a lead sheet — the agent calls each lead and the analysis judges the goal." cta="Create campaign" onClick={() => setModal(true)} testId="campaigns-empty" />}
      <div className="space-y-3">
        {list?.map((c) => {
          const leadType = c.lead_type || c.audience_type || "farmer";
          const sm = c.success_metric || {};
          const target = sm.target ?? 0;
          const current = sm.current ?? 0;
          const goalPct = target ? Math.min(100, Math.round((current / target) * 100)) : 0;
          const processedPct = c.total ? Math.min(100, Math.round(((c.processed ?? 0) / c.total) * 100)) : 0;
          const districts = c.region?.districts || [];
          const region = c.region?.state || c.state || "";
          return (
            <div key={c.id} className="glass p-5 rise" data-testid={`campaign-card-${c.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${leadType === "farmer" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}><Megaphone className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/dashboard/campaigns/${c.id}`} className="font-display font-semibold text-slate-100 hover:text-emerald-300" data-testid={`campaign-link-${c.id}`}>{c.name}</Link>
                    <StatusPill status={c.status} />
                    <span className={leadType === "farmer" ? "pill" : "pill-gold"} data-testid={`campaign-lead-type-pill-${c.id}`}>{leadType}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {c.agent_name || "No agent"} → {region || "—"}{districts.length ? ` · ${districts.length} district${districts.length === 1 ? "" : "s"}` : ""}{c.goal ? ` · ${c.goal}` : ""}
                  </p>
                  {c.status === "running" && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-500 mb-1"><span>Leads processed</span><span>{c.processed ?? 0}/{c.total ?? 0}</span></div>
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-amber-400 transition-[width] duration-700" style={{ width: `${processedPct}%` }} /></div>
                    </div>
                  )}
                  {target > 0 && (
                    <div className="mt-3" data-testid={`campaign-goal-progress-${c.id}`}>
                      <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-500 mb-1"><span>Goal conversions</span><span>{current}/{target} · {goalPct}%</span></div>
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-[width] duration-700" style={{ width: `${goalPct}%` }} /></div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-4 text-center md:text-right">
                  {[["Leads", c.total ?? 0], ["Goal", target ? `${current}/${target}` : "—"], ["POs", c.orders ?? 0], ["Reached", fmtNum(c.reached)]].map(([l, v]) => <div key={l}><p className="text-[10px] font-mono uppercase text-slate-500">{l}</p><p className="font-display font-semibold text-slate-100">{v}</p></div>)}
                </div>
                <div className="flex gap-2 shrink-0 items-center">
                  {c.status === "running" && <button onClick={() => ctl(c.id, "pause")} className="btn-ghost btn-sm" data-testid={`campaign-pause-${c.id}`}><Pause className="w-3.5 h-3.5" /> Pause</button>}
                  {c.status === "paused" && <button onClick={() => ctl(c.id, "resume")} className="btn-primary btn-sm" data-testid={`campaign-resume-${c.id}`}><Play className="w-3.5 h-3.5" /> Resume</button>}
                  {c.status !== "running" && c.status !== "paused" && <button onClick={() => ctl(c.id, "run")} className="btn-primary btn-sm" data-testid={`campaign-run-${c.id}`}><Play className="w-3.5 h-3.5" /> {c.status === "completed" ? "Re-run" : "Run"}</button>}
                  <Link to={`/dashboard/campaigns/${c.id}`} className="btn-ghost btn-sm" data-testid={`campaign-open-${c.id}`}>Open</Link>
                  <button onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-300 p-2" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={modal} onClose={close} title={`New campaign · step ${step} of 2 — ${step === 1 ? "Campaign" : "Leads"}`} wide testId="campaign-modal">
        <div className="flex items-center gap-2 mb-5" data-testid="campaign-wizard-steps">
          {[["1", "Campaign"], ["2", "Leads"]].map(([n, label]) => {
            const reachable = n === "1" || step1Valid;
            return (
              <button key={n} type="button" disabled={!reachable} onClick={() => reachable && setStep(Number(n))} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${step === Number(n) ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200" : "border-white/10 text-slate-500"}`} data-testid={`campaign-wizard-step-${n}`} title={reachable ? "" : "Add a name, state and agent first"}>
                <span className="font-mono">{n}</span>{label}
              </button>
            );
          })}
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <div><label className="field-label">Campaign name</label><input className="field" placeholder="Koshi corn seed — Saharsa farmers" value={form.campaign_name} onChange={(e) => set({ campaign_name: e.target.value })} data-testid="campaign-name-input" /></div>
            <div>
              <label className="field-label">Lead type</label>
              <div className="grid grid-cols-2 gap-2">
                {[["farmer", "Farmer"], ["retailer", "Retailer"]].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => pickLeadType(k)} data-testid={`campaign-lead-type-${k}`} className={`rounded-xl px-4 py-2.5 text-sm border transition-colors ${form.lead_type === k ? (k === "farmer" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" : "bg-amber-500/15 border-amber-500/40 text-amber-200") : "bg-white/[0.03] border-white/10 text-slate-400 hover:text-slate-200"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="field-label">State</label><Select value={form.state} onChange={(v) => set({ state: v, districts: [] })} options={stateOptions} placeholder="Select state" testId="campaign-state-select" /></div>
              <div><label className="field-label">Agent · {form.lead_type === "farmer" ? "farmer" : "retailer"} audience</label><Select value={form.agent_id} onChange={(v) => set({ agent_id: v })} options={agentChoices.map((a) => ({ value: a.id, label: `${a.name} (${a.state || a.audience}${a.status && a.status !== "published" ? ` · ${a.status}` : ""})` }))} placeholder={agentChoices.length ? "Select agent" : "No matching agent"} testId="campaign-agent-select" />
                {agentChoices.length === 0 && <p className="text-xs text-amber-300 mt-1">No {form.lead_type} agents yet — <Link to="/dashboard/agents/new" className="underline">create one</Link>.</p>}
              </div>
            </div>
            <div>
              <label className="field-label">Districts · {form.state || "pick a state first"} <span className="text-slate-600">({form.districts.length} selected)</span></label>
              <div className="flex flex-wrap gap-1.5 min-h-[38px]" data-testid="campaign-districts">
                {districtChoices.map((d) => (
                  <button key={d} type="button" onClick={() => toggleDistrict(d)} data-testid={`campaign-district-${slug(d)}`} className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${form.districts.includes(d) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200" : "bg-white/[0.03] border-white/10 text-slate-400 hover:text-slate-200"}`}>{d}</button>
                ))}
                {!districtChoices.length && <p className="text-xs text-slate-600">{opts ? (form.state ? "No district list for this state — type them in the sheet instead." : "Select a state to load its districts.") : "Loading districts…"}</p>}
              </div>
            </div>
            <div><label className="field-label">Goal <span className="text-slate-600">— the post-call analysis judges every conversation against this</span></label><textarea className="field min-h-[76px]" placeholder="e.g. Farmer agrees to a corn seed order request — the post-call analysis judges this" value={form.goal} onChange={(e) => set({ goal: e.target.value })} data-testid="campaign-goal-input" /></div>
            <div>
              <label className="field-label">Success metric · goal conversions target</label>
              <div className="grid grid-cols-3 gap-2">
                {TARGETS.map((n) => (
                  <button key={n} type="button" onClick={() => set({ success_target: n })} data-testid={`campaign-success-${n}`} aria-pressed={targetNumber === n} className={`glass-soft p-3 text-left transition-colors ${targetNumber === n ? "border-emerald-500/50 bg-emerald-500/10" : "hover:border-white/20"}`}>
                    <p className={`font-display text-xl font-bold ${targetNumber === n ? "text-emerald-300" : "text-slate-100"}`}>{n}</p>
                    <p className="text-[11px] text-slate-500">goal conversions</p>
                  </button>
                ))}
              </div>
              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                <label htmlFor="campaign-success-custom" className="text-[11px] font-mono uppercase tracking-wide text-slate-500">Custom target</label>
                <input id="campaign-success-custom" type="number" min="1" max="100000" step="1" className="field w-32" value={form.success_target} onChange={(e) => set({ success_target: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="campaign-success-custom" />
                <span className="text-[11px] text-slate-500">Any positive whole number</span>
              </div>
              {!targetValid && <p className="text-[11px] text-rose-300 mt-1">Enter a whole number greater than 0.</p>}
            </div>
            <div className="flex items-center justify-between glass-soft px-4 py-3">
              <div><p className="text-sm text-slate-200">Auto-pause when target reached</p><p className="text-[11px] text-slate-500">Stops the sequence once {targetValid ? form.success_target : "the target"} goals are achieved.</p></div>
              <Toggle on={form.auto_pause} onChange={(v) => set({ auto_pause: v })} testId="campaign-auto-pause" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={close} className="btn-ghost flex-1">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!step1Valid} className="btn-primary flex-1" data-testid="campaign-wizard-next">Next · Leads <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="field-label">Lead sheet (.xlsx or .csv)</label>
              <input type="file" accept=".xlsx,.csv" className="field" onChange={(e) => set({ file: e.target.files?.[0] || null })} data-testid="campaign-file" />
              <p className="text-[11px] text-slate-500 mt-1.5">Required: name, phone. Optional: retailer / retailer_name / retailer_id (farmers tagged to a retailer), block, crop, lat, lng. <button type="button" className="underline" data-testid="campaign-template-btn" onClick={async () => { const res = await api.get("/campaigns/lead-template", { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "agrivoice-leads.xlsx"; a.click(); URL.revokeObjectURL(url); }}><Download className="w-3 h-3 inline -mt-0.5" /> Download the template</button></p>
              {form.file && <p className="text-xs text-slate-300 mt-2" data-testid="campaign-file-name">{form.file.name} · {fileSize(form.file.size)}</p>}
            </div>
            <p className="text-[11px] text-slate-500">Leads will be called by the selected agent, judged against the goal, and followed up per the agent's outreach sequence.</p>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setStep(1)} className="btn-ghost" data-testid="campaign-wizard-back"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={() => create(false)} disabled={!step2Valid || busy} className="btn-ghost flex-1" data-testid="campaign-save-btn">Save draft</button>
              <button onClick={() => create(true)} disabled={!step2Valid || busy} className="btn-primary flex-1" data-testid="campaign-launch-btn"><Play className="w-4 h-4" /> Save &amp; launch</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
